import React from "react";
import axiosInstance from "../../Services/axiosInstance.ts";
import { API_ENDPOINTS } from "../../Api/UploadData.ts";

interface FileResponse {
  dataset_name: string;
  file_name: string;
  total_fields: number;
  total_records: number;
  created_by: number;
  id: number;
  created_at: string;
  modified_at: string;
}

export const FileUploadStatus: React.FC<{
  file: File;
  progress: number;
  onDelete: () => void;
}> = ({ file, progress, onDelete }) => {
  return (
    <div className="border border-gray-200 rounded-lg p-4 my-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-gray-500"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div>
            <p className="font-medium">{file.name}</p>
            <p className="text-sm text-gray-500">
              {(file.size / 1024).toFixed(0)} KB
            </p>
          </div>
        </div>

        <button
          onClick={onDelete}
          className="text-red-500 hover:text-red-700"
          aria-label="Delete file"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        </button>
      </div>

      <div className="w-full mt-2">
        <div className="bg-[#D3E0E3] h-2 w-full rounded-full overflow-hidden">
          <div
            className="bg-[#4AA3BA] h-full rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
        <div className="text-right text-sm mt-1">{progress}%</div>
      </div>
    </div>
  );
};

// Updated function for single file upload (keeping for backward compatibility)
export const uploadFileWithProgress = (
  file: File,
  datasetName: string,
  templateId: number,
  onProgress: (progress: number) => void
): Promise<FileResponse> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("dataset_name", datasetName);
  formData.append("template_id", templateId.toString());

  return new Promise((resolve, reject) => {
    axiosInstance
      .post(API_ENDPOINTS.POST_DATA, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        params: { dataset_name: datasetName, template_id: templateId },
        onUploadProgress: (event) => {
          if (event.total) {
            const progressPercent = Math.round((event.loaded / event.total) * 100);
            onProgress(progressPercent === 100 ? 99 : progressPercent);
          }
        },
      })
      .then((response) => {
        onProgress(100);
        resolve(response.data);
      })
      .catch((error) => reject(error));
  });
};

// New function for multiple file upload
export const uploadMultipleFilesWithProgress = (
  files: File[],
  datasetName: string,
  templateId: number,
  onProgress: (progress: number) => void,
  usage: string,
  datasource?: string,
  iceberg_table_name?: string
): Promise<FileResponse[]> => {
  return new Promise((resolve, reject) => {
    let data: FormData | object;

    if (datasource !== "datalake") {
      const formData = new FormData();
      files.forEach((file) => formData.append("files", file));
      data = formData;
    } else {
      data = {};
    }

    axiosInstance
      .post(API_ENDPOINTS.POST_DATA, data, {
        headers: {
          "Content-Type": datasource !== "datalake" ? "multipart/form-data" : "application/json",
        },
        params: {
          dataset_name: datasetName,
          usage,
          data_source: datasource === "file" ? "csv" : datasource,
          ...(datasource !== "datalake" && { template_id: templateId }),
          ...(datasource === "datalake" && { iceberg_table_name }),
        },
        onUploadProgress: (event) => {
          if (event.total) {
            const progressPercent = Math.round((event.loaded / event.total) * 100);
            onProgress(progressPercent === 100 ? 99 : progressPercent);
          }
        },
      })
      .then((response) => {
        onProgress(100);
        // Assuming the API returns an array of FileResponse objects
        resolve(response.data);
      })
      .catch((error) => reject(error));
  });
};